/**
 * Staff auth + axios instance.
 * Bearer token persisted in localStorage so the staff PWA stays signed in
 * across sessions on the phone home screen.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'staff_token';

export const staffApi = axios.create({ baseURL: `${API}/api` });

staffApi.interceptors.request.use(cfg => {
  const token = localStorage.getItem(STORAGE_KEY);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export function setStaffToken(token) {
  if (token) localStorage.setItem(STORAGE_KEY, token);
  else localStorage.removeItem(STORAGE_KEY);
}
export function getStaffToken() { return localStorage.getItem(STORAGE_KEY); }

// Auth endpoints
export const apiLogin = (email, password) => staffApi.post('/auth/login', { email, password }).then(r => r.data);
export const apiRegister = (name, email, password, pin) => staffApi.post('/auth/register', { name, email, password, pin }).then(r => r.data);
export const apiValidatePin = (pin) => staffApi.post('/auth/validate-pin', { pin }).then(r => r.data);
export const apiMe = () => staffApi.get('/auth/me').then(r => r.data);
export const apiLogout = () => staffApi.post('/auth/logout').then(r => r.data).catch(() => null);
export const apiForgot = (email) => staffApi.post('/auth/forgot-password', { email }).then(r => r.data);
export const apiReset = (token, new_password) => staffApi.post('/auth/reset-password', { token, new_password }).then(r => r.data);

// Admin staff CRUD
export const adminListStaff = (password) => staffApi.get('/admin/staff', { params: { password } }).then(r => r.data);
export const adminInviteStaff = (password, payload) => staffApi.post('/admin/staff/invite', { password, ...payload }).then(r => r.data);
export const adminUpdateStaff = (password, userId, payload) => staffApi.put(`/admin/staff/${userId}`, { password, ...payload }).then(r => r.data);
export const adminDeleteStaff = (password, userId) => staffApi.delete(`/admin/staff/${userId}`, { params: { password } }).then(r => r.data);
export const adminRevokeInvite = (password, inviteId) => staffApi.delete(`/admin/staff/invites/${inviteId}`, { params: { password } }).then(r => r.data);
export const adminHoursChart = (password) => staffApi.get('/admin/staff/hours-chart', { params: { password } }).then(r => r.data);
export const adminHoursDetail = (password) => staffApi.get('/admin/staff/hours-detail', { params: { password } }).then(r => r.data);
export const adminListPayPeriods = (password, userId) => staffApi.get(`/admin/staff/pay-periods/${userId}`, { params: { password } }).then(r => r.data);
export const adminUpsertPayPeriod = (password, userId, periodStart, payload) =>
  staffApi.put(`/admin/staff/pay-periods/${userId}/${periodStart}`, { password, ...payload }).then(r => r.data);
export const adminUpdateHoursEntry = (password, entryId, payload) =>
  staffApi.put(`/admin/staff/hours/${entryId}`, { password, ...payload }).then(r => r.data);
export const adminDeleteHoursEntry = (password, entryId) =>
  staffApi.delete(`/admin/staff/hours/${entryId}`, { params: { password } }).then(r => r.data);

/* ── Staff tasks (admin side) ─────────────────────────────────── */
export const adminListTasks = (password, params = {}) =>
  staffApi.get('/admin/staff-tasks', { params: { password, ...params } }).then(r => r.data);
export const adminCreateTask = (password, payload) =>
  staffApi.post('/admin/staff-tasks', { password, ...payload }).then(r => r.data);
export const adminUpdateTask = (password, taskId, payload) =>
  staffApi.put(`/admin/staff-tasks/${taskId}`, { password, ...payload }).then(r => r.data);
export const adminDeleteTask = (password, taskId) =>
  staffApi.delete(`/admin/staff-tasks/${taskId}`, { params: { password } }).then(r => r.data);
export const adminPostTaskMessage = (password, taskId, body) =>
  staffApi.post(`/admin/staff-tasks/${taskId}/message`, { password, body }).then(r => r.data);
export const adminMarkTaskRead = (password, taskId) =>
  staffApi.post(`/admin/staff-tasks/${taskId}/mark-read`, { password }).then(r => r.data);
export const adminTaskNotifications = (password) =>
  staffApi.get('/admin/staff-tasks/notifications', { params: { password } }).then(r => r.data);

/* ── Staff tasks (mobile / "me" side) ─────────────────────────── */
export const meTasks = () => staffApi.get('/me/staff-tasks').then(r => r.data);
export const meMarkTaskDone = (taskId, note = '') =>
  staffApi.post(`/me/staff-tasks/${taskId}/done`, { note }).then(r => r.data);
export const meReopenTask = (taskId) =>
  staffApi.post(`/me/staff-tasks/${taskId}/reopen`).then(r => r.data);
export const mePostTaskMessage = (taskId, body) =>
  staffApi.post(`/me/staff-tasks/${taskId}/message`, { body }).then(r => r.data);
export const meMarkTaskRead = (taskId) =>
  staffApi.post(`/me/staff-tasks/${taskId}/mark-read`).then(r => r.data);
export const meTaskNotifications = () =>
  staffApi.get('/me/staff-tasks/notifications').then(r => r.data);
export const meTasksForProject = (projectId) =>
  staffApi.get(`/me/staff-tasks/by-project/${projectId}`).then(r => r.data);

// Staff "me" endpoints (powered by Bearer JWT)
export const meDashboard = () => staffApi.get('/me/dashboard').then(r => r.data);
export const mePayPeriods = () => staffApi.get('/me/pay-periods').then(r => r.data);
export const meProjects = () => staffApi.get('/me/projects').then(r => r.data);
export const meProject = (projectId) => staffApi.get(`/me/projects/${projectId}`).then(r => r.data);
export const meHours = (params = {}) => staffApi.get('/me/hours', { params }).then(r => r.data);
export const meLogHours = (payload) => staffApi.post('/me/hours', payload).then(r => r.data);
export const meDeleteHours = (entryId, projectId) => staffApi.delete(`/me/hours/${entryId}`, { params: { project_id: projectId } }).then(r => r.data);
export const meUpdateProfile = (payload) => staffApi.put('/me/profile', payload).then(r => r.data);

/** Format any error from a staffApi response into a string. */
export function fmtErr(e, fallback = 'Something went wrong') {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(' ');
  if (d?.msg) return d.msg;
  return e?.message || fallback;
}
